import debounce from "lodash/debounce";
import { useCallback } from "react";

import { showErrorToast } from "#app/notifications";
import { useCompanyModelType } from "#modules/company/view/useCompanyModelType";
import { useUpdatePart } from "#modules/parts/view/useUpdatePart";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { useUpdateMaterial } from "#modules/quoteRequestLines/useUpdateMaterial";
import { useUpdateQuoteRequestLineQuantity } from "#modules/quoteRequestLines/view/useUpdateQuoteRequestLineQuantity";
import { usePredictMaterial } from "#modules/quoteRequestLines/views/usePredictMaterial";
import { usePredictProcessSteps } from "#modules/quoteRequestLines/views/usePredictProcessSteps";
import { PredictionModelTypeSchema } from "shared";

import { useCouldCallPredict } from "./views/QuoteRequestLine/useCouldCallPredict";

export const useUpdatePredictionData = ({
  quoteRequestLine,
  isChild,
}: {
  quoteRequestLine: QuoteRequestLineDto;
  isChild?: boolean;
}) => {
  const modelType = useCompanyModelType();

  const { predictProcessSteps, isError: isPredictionError } = usePredictProcessSteps();

  const { predictMaterial } = usePredictMaterial();

  const couldCallPredict = useCouldCallPredict({
    quoteRequestLine,
  });

  const { updateQuoteRequestLineQuantity } = useUpdateQuoteRequestLineQuantity();

  const { updatePart } = useUpdatePart(quoteRequestLine.part.id);

  const { updateMaterial } = useUpdateMaterial({
    onSuccess: async () => {
      if (!couldCallPredict && modelType !== PredictionModelTypeSchema.enum.CLUSTERING) {
        showErrorToast({
          title: "Impossible de prédire le coût de cette pièce",
          message: "Veuillez vérifier les données renseignées puis recharger la page",
          autoClose: 5000,
        });

        return;
      }
    },
  });

  const updateQuantityCall = (quantity: number | undefined) => {
    if (isChild && quoteRequestLine.partId) {
      updatePart({
        id: quoteRequestLine.partId,
        payload: { quantityInAssembly: quantity ?? 1 },
      });
      return;
    }

    return updateQuoteRequestLineQuantity({
      id: quoteRequestLine.id,
      payload: {
        quantity: quantity ?? 1,
      },
    });
  };

  // oxlint-disable-next-line react-hooks/exhaustive-deps
  const updateQuantityDebounced = useCallback(debounce(updateQuantityCall, 800), []);

  return {
    updateQuantity: updateQuantityDebounced,
    updateMaterial,
    isPredictionError,
    predictProcessSteps,
    predictMaterial,
  };
};
